import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { vehicleActions } from 'state/modules/vehicles';
import { useStyles } from './ChangeVehicleStatusModal.styled';
import { VehicleStatusSelect, VehicleReasonsSelect } from 'components/DataFields';
import { CheckboxField, DateTimePickerField, TextareaField } from 'components/FormFields';
import { getDateTimezoneFormat } from 'utils/utils';
import ChangeVehicleStatusReservationsConflictModal from '../ChangeVehicleStatusReservationsConflictModal';
import ChangeVehicleStatusBulkReservationsConflictModal from '../ChangeVehicleStatusBulkReservationsConflictModal';
import MessagesModal from 'components/Modals/Messages';

const statuses = ['INSHOP', 'RETIRED'];

const ResultModalState = Object.freeze({
  SUCCESS: Symbol('SUCCESS'),
  FAILED: Symbol('FAILED'),
});

const ChangeVehicleStatusModal = ({
  title,
  vehicle,
  vehicleId,
  vehicleIds,
  vehicleGroupId,
  timezone,
  open,
  handleClick,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showBulkConflictModal, setShowBulkConflictModal] = useState(false);
  const [changeVehicleState, setChangeVehicleState] = useState(null);
  const changeVehicleStatusErrors = useSelector((state) => state.vehicles.errors);
  const changeVehicleStatusConflict = useSelector((state) => state.vehicles.conflictingVehicles);
  const inShopManagementEnabled = useSelector(
    (state) => state.config?.settings?.core?.vehicle?.inshop_management || false
  );
  const {
    onboarding: { errors },
    fetchingChangeStatus,
    fetchedChangeStatusSuccess,
  } = useSelector((state) => state.vehicles);
  const { localeFormatLower } = useSelector((state) => state.organizations);

  const ChangeVehicleStatusSchema = Yup.object().shape({
    status: Yup.string().required(i18n.t('common.required_field')),
    start_now: Yup.boolean(),
    return_date_not_set: Yup.boolean(),
    start_time: Yup.string().when('start_now', {
      is: false,
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
    end_time: Yup.string().when('return_date_not_set', {
      is: false,
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
    offline_reason: Yup.string().when('status', {
      is: (val) => statuses.indexOf(val) !== -1,
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
    description: Yup.string().when('status', {
      is: (val) => statuses.indexOf(val) !== -1,
      then: Yup.string()
        .required(i18n.t('common.required_field'))
        .max(140, i18n.t('common.max_character', { max: 140 })),
    }),
  });

  useEffect(() => {
    if (fetchedChangeStatusSuccess) {
      if (inShopManagementEnabled) {
        setChangeVehicleState(ResultModalState.SUCCESS);
      } else {
        handleClick();
      }
    }
  }, [fetchedChangeStatusSuccess, handleClick, dispatch, vehicleId, inShopManagementEnabled]);

  // Reset conflicting vehicles state when modal closes
  useEffect(() => {
    if (!open) {
      dispatch(vehicleActions.resetChangeStatus());
      setShowConflictModal(false);
      setShowBulkConflictModal(false);
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (changeVehicleStatusErrors.hasConflictingReservations) {
      setShowConflictModal(true);
    } else if (changeVehicleStatusConflict && changeVehicleStatusConflict.length > 0) {
      setShowBulkConflictModal(true);
    } else if (inShopManagementEnabled && Object.keys(changeVehicleStatusErrors).length > 0) {
      setChangeVehicleState(ResultModalState.FAILED);
    }
  }, [changeVehicleStatusErrors, inShopManagementEnabled, changeVehicleStatusConflict]);

  const handleCloseMessageModal = () => {
    setChangeVehicleState(null);
    handleClick();
  };

  const submit = (values) => {
    const { start_now, return_date_not_set, ...data } = values;
    // delete date fields if checks are selected
    if (start_now) delete data.start_time;
    if (return_date_not_set) delete data.end_time;

    // remove empty fields
    Object.keys(data).forEach((key) => !data[key] && data[key] !== false && delete data[key]);

    if (vehicleGroupId) {
      data.vehicle_group_id = vehicleGroupId;
      dispatch(vehicleActions.changeMultipleVehicleStatus(data, inShopManagementEnabled));
    } else if (vehicleIds && vehicleIds.length) {
      if (vehicleIds.length > 1) {
        data.vehicle_ids = vehicleIds;
        dispatch(vehicleActions.changeMultipleVehicleStatus(data, inShopManagementEnabled));
      } else {
        dispatch(vehicleActions.changeVehicleStatus(data, vehicleIds[0], inShopManagementEnabled));
      }
    } else if (vehicleId) {
      dispatch(vehicleActions.changeVehicleStatus(data, vehicleId, inShopManagementEnabled));
    }
  };
  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const getDateFormat = () => getDateTimezoneFormat(`${localeFormatLower} h:mm a`, timezone);
  const toTitleCase = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      loading={fetchingChangeStatus}
      enterButtonLabel={i18n.t('vehicle.change_status')}
      title={title || i18n.t('vehicle.change_vehicle_status')}
      maxWidth={700}
    >
      <Formik
        initialValues={{
          status: vehicle?.status || '',
          start_now: true,
          return_date_not_set: true,
          start_time: moment().format(),
          end_time: moment().add(1, 'day').format(),
          offline_reason: vehicle?.offline_reason || '',
          description: '',
        }}
        innerRef={formRef}
        validationSchema={ChangeVehicleStatusSchema}
        onSubmit={submit}
      >
        {({ handleSubmit, values }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Box mt={1}>
              <Field
                name="status"
                margin="none"
                label="Status"
                omitFields={['INACTIVE']}
                component={VehicleStatusSelect}
              />
            </Box>
            <Box mt={2} mb={1}>
              <Typography variant="h5" className={classes.subtitle}>
                Data Range
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              {i18n.t('vehicle.date_range_description')}
            </Typography>
            <Box mt={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    disabled={values.start_now}
                    editable={!values.start_now}
                    name="start_time"
                    id="start_time"
                    minDate={new Date()}
                    className={classes.dateField}
                    component={DateTimePickerField}
                    dateFormat={getDateFormat()}
                    asyncErrors={errors?.start_time}
                    label={
                      !values.start_now
                        ? `Start ${toTitleCase(values.status)} status`
                        : i18n.t('vehicle.start_date')
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="end_time"
                    id="end_time"
                    disabled={values.return_date_not_set}
                    editable={!values.return_date_not_set}
                    component={DateTimePickerField}
                    dateFormat={getDateFormat()}
                    asyncErrors={errors?.end_time}
                    className={classes.dateField}
                    minDate={moment(values.start_time).toDate() || new Date()}
                    label={
                      !values.return_date_not_set
                        ? `End ${toTitleCase(values.status)} status`
                        : i18n.t('vehicle.end_date')
                    }
                  />
                </Grid>
              </Grid>
            </Box>
            <Box mt={1}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Field
                    name="start_now"
                    labelPlacement="end"
                    component={CheckboxField}
                    className={classes.checkField}
                    label={i18n.t('vehicle.start_now')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center">
                    <Field
                      name="return_date_not_set"
                      labelPlacement="end"
                      component={CheckboxField}
                      className={classes.checkField}
                      label={i18n.t('vehicle.return_date_not_set')}
                    />
                    <Tooltip title={i18n.t('vehicle.no_scheduled_completion_date')} placement="top">
                      <InfoIcon
                        color="primary"
                        style={{
                          fontSize: 16,
                          marginLeft: -15,
                          cursor: 'pointer',
                          position: 'relative',
                          top: '-15px',
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {statuses.indexOf(values.status) !== -1 && (
              <>
                <Box mt={1}>
                  <Field
                    margin="none"
                    name="offline_reason"
                    component={VehicleReasonsSelect}
                    className={classes.reasonSelect}
                    asyncErrors={errors?.offline_reason}
                    label={i18n.t('vehicle.offline_reason')}
                  />
                </Box>
                <Box mt={2}>
                  <Field
                    name="description"
                    component={TextareaField}
                    className={classes.reasonText}
                    asyncErrors={errors?.description}
                    label={i18n.t('vehicle.reason_description')}
                  />
                </Box>
              </>
            )}
            {showConflictModal && (
              <ChangeVehicleStatusReservationsConflictModal
                vehicleId={vehicleId}
                startTime={values.start_now ? moment().toISOString() : values.start_time}
                endTime={values.return_date_not_set ? null : values.end_time}
                open={showConflictModal}
                onClose={() => setShowConflictModal(false)}
                newStatus={values.status}
                offlineReason={values.offline_reason}
                description={values.description}
                onCloseAll={handleClick}
              />
            )}

            {showBulkConflictModal && (
              <ChangeVehicleStatusBulkReservationsConflictModal
                open={showBulkConflictModal}
                onClose={() => {
                  setShowBulkConflictModal(false);
                  dispatch(vehicleActions.resetChangeStatus());
                }}
                vehicles={changeVehicleStatusConflict}
                startTime={values.start_now ? moment().toISOString() : values.start_time}
                endTime={values.return_date_not_set ? null : values.end_time}
                newStatus={values.status}
                offlineReason={values.offline_reason}
                description={values.description}
                onCloseAll={handleClick}
              />
            )}

            {changeVehicleState === ResultModalState.SUCCESS && (
              <MessagesModal
                open={changeVehicleState === ResultModalState.SUCCESS}
                onClose={handleCloseMessageModal}
                type="success"
                title={i18n.t('vehicle.status_change_success_title')}
                message={i18n.t('vehicle.status_change_success_message')}
              />
            )}

            {changeVehicleState === ResultModalState.FAILED && (
              <MessagesModal
                open={changeVehicleState === ResultModalState.FAILED}
                onClose={handleCloseMessageModal}
                type="error"
                title={i18n.t('vehicle.status_change_error_title')}
                message={i18n.t('vehicle.status_change_error_message')}
              />
            )}
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

ChangeVehicleStatusModal.propTypes = {
  title: PropTypes.string,
  vehicle: PropTypes.object,
  timezone: PropTypes.string,
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  vehicleId: PropTypes.number,
  vehicleIds: PropTypes.array,
  vehicleGroupId: PropTypes.number,
};

ChangeVehicleStatusModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default ChangeVehicleStatusModal;
